import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { projectsApi } from '../../api/projects';
import { downloadProjectBackup } from '../../utils/downloadProjectBackup';
import { storiesApi } from '../../api/stories';
import { useApp } from '../../context/AppContext';
import ProjectTitleInput from '../Common/ProjectTitleInput';
import { styleCardsPath } from '../../utils/styleCardsPath';
import { STYLE_FILTER_BODY, STYLE_NOTES } from '../../utils/writerCopy';
import './ProjectSettingsPanel.css';

const formatCreatedDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
  } catch {
    return '';
  }
};

const ProjectSettingsPanel = ({ project, storyCount, isOpen, onClose, onDeleteProject }) => {
  const { refreshProjects, currentStory, setCurrentStory, setCurrentProject } = useApp();
  const navigate = useNavigate();
  const [name, setName] = useState(project?.name || '');
  const [storyName, setStoryName] = useState(currentStory?.name || '');
  const [renameSaving, setRenameSaving] = useState(false);
  const [storyRenameSaving, setStoryRenameSaving] = useState(false);
  const [renameError, setRenameError] = useState(null);
  const [storyRenameError, setStoryRenameError] = useState(null);
  const [pendingConvert, setPendingConvert] = useState(null); // 'to-single' | 'to-series' | null
  const [converting, setConverting] = useState(false);
  const [convertError, setConvertError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(null);
  const [backingUp, setBackingUp] = useState(false);
  const [backupError, setBackupError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setName(project?.name || '');
      setStoryName(currentStory?.name || '');
      setRenameError(null);
      setStoryRenameError(null);
      setPendingConvert(null);
      setConvertError(null);
      setExportError(null);
      setBackupError(null);
    }
  }, [isOpen, project?.id, project?.name, currentStory?.id, currentStory?.name]);

  if (!isOpen || !project) return null;

  const isSeries = !!project.is_series;
  const created = formatCreatedDate(project.created_at);

  const commitRename = async () => {
    const trimmed = name.trim();
    if (!trimmed || trimmed === project.name) {
      setName(project.name || '');
      return;
    }
    try {
      setRenameSaving(true);
      setRenameError(null);
      await projectsApi.update(project.id, { name: trimmed });
      await refreshProjects?.();
    } catch (err) {
      setRenameError(err?.message || 'Failed to rename project');
      setName(project.name || '');
    } finally {
      setRenameSaving(false);
    }
  };

  const commitStoryRename = async () => {
    if (!currentStory?.id) return;
    const trimmed = storyName.trim();
    if (!trimmed || trimmed === currentStory.name) {
      setStoryName(currentStory.name || '');
      return;
    }
    try {
      setStoryRenameSaving(true);
      setStoryRenameError(null);
      await storiesApi.update(currentStory.id, { name: trimmed });
      setCurrentStory?.({
        ...currentStory,
        name: trimmed,
        project_id: currentStory.project_id || project.id,
      });
      if (!isSeries && storyCount === 1) {
        setCurrentProject?.({ ...project, name: trimmed });
      }
      await refreshProjects?.();
    } catch (err) {
      setStoryRenameError(err?.message || 'Failed to rename story');
      setStoryName(currentStory.name || '');
    } finally {
      setStoryRenameSaving(false);
    }
  };

  const handleStoryNameKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.target.blur();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setStoryName(currentStory?.name || '');
      e.target.blur();
    }
  };

  const handleNameKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.target.blur();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setName(project.name || '');
      e.target.blur();
    }
  };

  const handleBackup = async () => {
    try {
      setBackingUp(true);
      setBackupError(null);
      await downloadProjectBackup(project);
    } catch (err) {
      setBackupError(err?.message || 'This backup could not be prepared. Try again.');
    } finally {
      setBackingUp(false);
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      setExportError(null);
      const response = await projectsApi.export(project.id);
      const blob = await response.blob();
      // Prefer the server's filename; fall back to a client-side one.
      const disposition = response.headers.get('Content-Disposition') || '';
      const match = disposition.match(/filename="?([^";]+)"?/);
      const fallbackSlug = (project.name || 'project')
        .toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'project';
      const filename = match
        ? match[1]
        : `${fallbackSlug}-export-${new Date().toISOString().slice(0, 10)}.md`;
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setExportError(err?.message || 'Failed to export project');
    } finally {
      setExporting(false);
    }
  };

  const openStyleNotes = () => {
    if (!currentStory?.id) return;
    onClose?.();
    navigate(styleCardsPath(currentStory.id));
  };

  const handleConfirmConvert = async () => {
    try {
      setConverting(true);
      setConvertError(null);
      if (pendingConvert === 'to-single') {
        await projectsApi.convertToSingleBook(project.id);
      } else if (pendingConvert === 'to-series') {
        await projectsApi.convertToSeries(project.id, {});
      }
      window.location.reload();
    } catch (err) {
      setConvertError(err?.message || 'Conversion failed');
      setConverting(false);
    }
  };

  return (
    <div className="project-settings-backdrop" onClick={onClose}>
      <div
        className="project-settings-panel"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="project-settings-title"
      >
        <div className="project-settings-header">
          <h2 id="project-settings-title">Project Settings</h2>
          <button
            className="project-settings-close"
            onClick={onClose}
            aria-label="Close project settings"
          >
            ×
          </button>
        </div>

        <div className="project-settings-content">
          <section className="project-settings-section">
            <label className="project-settings-label" htmlFor="project-name-input">
              Project name
            </label>
            <ProjectTitleInput
              id="project-name-input"
              className="project-settings-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onBlur={commitRename}
              onKeyDown={handleNameKeyDown}
              disabled={renameSaving}
              maxLength={120}
            />
            {renameError && (
              <div className="project-settings-error" role="alert">{renameError}</div>
            )}
          </section>

          {!isSeries && currentStory?.id && (
            <section className="project-settings-section">
              <label className="project-settings-label" htmlFor="story-name-input">
                Story name
              </label>
              <input
                id="story-name-input"
                type="text"
                className="project-settings-input"
                value={storyName}
                onChange={(e) => setStoryName(e.target.value)}
                onBlur={commitStoryRename}
                onKeyDown={handleStoryNameKeyDown}
                disabled={storyRenameSaving}
                maxLength={120}
              />
              {storyRenameError && (
                <div className="project-settings-error" role="alert">{storyRenameError}</div>
              )}
            </section>
          )}

          {currentStory?.id && (
            <section className="project-settings-section">
              <button
                type="button"
                className="project-settings-export project-settings-style-notes"
                onClick={openStyleNotes}
              >
                {STYLE_NOTES}
              </button>
              <div className="project-settings-hint">{STYLE_FILTER_BODY}</div>
            </section>
          )}

          <section className="project-settings-section">
            <div className="project-settings-label">Project type</div>
            <div className={`project-type-card ${isSeries ? 'project-type-card--series' : 'project-type-card--standalone'}`}>
              <div className="project-type-badges">
                {isSeries ? (
                  <span className="project-type-badge project-type-badge--series">SERIES</span>
                ) : (
                  <span className="project-type-badge project-type-badge--standalone">NOVEL</span>
                )}
              </div>
              <div className="project-type-meta">
                {isSeries && typeof storyCount === 'number' && (
                  <span>{storyCount} {storyCount === 1 ? 'book' : 'books'}</span>
                )}
                {isSeries && typeof storyCount === 'number' && created && <span> · </span>}
                {created && <span>created {created}</span>}
              </div>

              {pendingConvert === null && (
                <button
                  className="project-type-convert-link"
                  onClick={() => setPendingConvert(isSeries ? 'to-single' : 'to-series')}
                  disabled={converting}
                >
                  {isSeries ? 'Convert to single book →' : 'Convert to series →'}
                </button>
              )}
            </div>

            {pendingConvert === 'to-single' && (
              <div className="project-convert-confirm" role="region" aria-label="Confirm convert to single book">
                <h3>Convert to single book?</h3>
                <div className="project-convert-block">
                  <div className="project-convert-block__heading">What changes</div>
                  <ul>
                    <li>Series layout will switch to single-book layout</li>
                    <li>Series Planning section will be hidden</li>
                    <li>Series-specific tools (Dashboard, series Bible) will be hidden</li>
                  </ul>
                </div>
                <div className="project-convert-block">
                  <div className="project-convert-block__heading">What stays</div>
                  <ul>
                    <li>All your sessions, notes, and conversations</li>
                    <li>All book content — nothing is deleted</li>
                    <li>Everything is saved and restored if you convert back to series</li>
                  </ul>
                </div>
                {convertError && (
                  <div className="project-settings-error" role="alert">{convertError}</div>
                )}
                <div className="project-convert-actions">
                  <button
                    className="project-convert-btn project-convert-btn--secondary"
                    onClick={() => setPendingConvert(null)}
                    disabled={converting}
                  >
                    Cancel
                  </button>
                  <button
                    className="project-convert-btn project-convert-btn--primary"
                    onClick={handleConfirmConvert}
                    disabled={converting}
                  >
                    {converting ? 'Converting…' : 'Convert to single book'}
                  </button>
                </div>
              </div>
            )}

            {pendingConvert === 'to-series' && (
              <div className="project-convert-confirm" role="region" aria-label="Confirm convert to series">
                <h3>Convert to series?</h3>
                <div className="project-convert-block">
                  <div className="project-convert-block__heading">What changes</div>
                  <ul>
                    <li>Sidebar will switch to series layout</li>
                    <li>Series Planning section will appear</li>
                    <li>Series Discovery session will be created</li>
                  </ul>
                </div>
                <div className="project-convert-block">
                  <div className="project-convert-block__heading">What stays</div>
                  <ul>
                    <li>All your existing sessions and content — nothing moves</li>
                    <li>Your current story becomes Book 1 automatically</li>
                  </ul>
                </div>
                {convertError && (
                  <div className="project-settings-error" role="alert">{convertError}</div>
                )}
                <div className="project-convert-actions">
                  <button
                    className="project-convert-btn project-convert-btn--secondary"
                    onClick={() => setPendingConvert(null)}
                    disabled={converting}
                  >
                    Cancel
                  </button>
                  <button
                    className="project-convert-btn project-convert-btn--primary"
                    onClick={handleConfirmConvert}
                    disabled={converting}
                  >
                    {converting ? 'Converting…' : 'Convert to series'}
                  </button>
                </div>
              </div>
            )}
          </section>

          <section className="project-settings-section">
            <div className="project-settings-label">Backup</div>
            <button
              type="button"
              className="project-settings-export"
              onClick={handleBackup}
              disabled={backingUp}
            >
              {backingUp ? 'Preparing backup…' : 'Download backup'}
            </button>
            <div className="project-settings-hint">
              Download a backup of this project. Keep the file somewhere safe.
            </div>
            {backupError && (
              <div className="project-settings-error" role="alert">{backupError}</div>
            )}
          </section>

          <section className="project-settings-section">
            <div className="project-settings-label">Export</div>
            <button
              type="button"
              className="project-settings-export"
              onClick={handleExport}
              disabled={exporting}
            >
              {exporting ? 'Exporting…' : 'Export project'}
            </button>
            <div className="project-settings-hint">
              Downloads everything in this project as a single markdown file.
            </div>
            {exportError && (
              <div className="project-settings-error" role="alert">{exportError}</div>
            )}
          </section>
        </div>

        <div className="project-settings-footer">
          {onDeleteProject && (
            <button
              type="button"
              className="project-settings-delete"
              onClick={onDeleteProject}
            >
              Delete project
            </button>
          )}
          <button className="project-settings-done" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProjectSettingsPanel;
